'use client';

import { useMemo, useState } from 'react';
import { Box, Flex, HStack, Link, Stack, Text, VStack } from '@chakra-ui/react';
import { IRecipe } from '@/_types';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { useTranslations } from 'next-intl';
import { ClockIcon } from '@/_assets/icon-components';
import { Rating, RoundedStar } from '@smastrom/react-rating';
import CRating from './CRating';
import Comments from './Comments';
import type { RecipeReviewsPayload } from './types';

function stripHtml(html: string) {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .trim();
}

export default function RecipeDetails({
  data,
  reviews,
}: {
  data: IRecipe;
  reviews?: RecipeReviewsPayload;
}) {
  const t = useTranslations();
  const currentLocale = useCustomLocale();
  const [showMore, setShowMore] = useState(false);

  const nutrients = useMemo(
    () =>
      [
        {
          value: data?.energy_from,
          label: t('energy_value'),
          display:
            data?.energy_to > 0
              ? `${data.energy_from} - ${data.energy_to} ${t('kcal')}`
              : `${data?.energy_from} ${t('kcal')}`,
        },
        {
          value: data?.proteins,
          label: t('proteins'),
          display: `${data?.proteins} ${t('grams')}`,
        },
        {
          value: data?.fats,
          label: t('fats'),
          display: `${data?.fats} ${t('grams')}`,
        },
        {
          value: data?.carbohydrates,
          label: t('carbohydrates'),
          display: `${data?.carbohydrates} ${t('grams')}`,
        },
      ].filter((item) => item.value && item.value > 0),
    [data, t],
  );

  const descriptionHtml = data?.description?.[currentLocale] || '';
  const descriptionText = stripHtml(descriptionHtml);
  const isLongDescription = descriptionText.length > 140;

  return (
    <Stack gap={0} align='stretch' w='full'>
      <Text
        as='h1'
        fontSize={{ base: 'xl', lg: '3xl' }}
        lineHeight={1.2}
        fontWeight={600}
      >
        {data?.title?.[currentLocale]}
      </Text>

      <Stack gap={0} w='full' mt={{ base: 1, lg: 3 }}>
        {(data?.weight || 0) > 0 ? (
          <Stack mt={1} gap={1}>
            <Text as='span' color='gray.700' fontWeight={500}>
              {data.weight} {t('grams')}
            </Text>
            {nutrients.length > 0 && (
              <HStack gap={6} mt={{ base: 4, lg: 0 }} mb={4}>
                {nutrients.map((nutrient, index) => (
                  <Flex key={index} fontSize='sm' flexDir='column' gap={0}>
                    <Text color='text.muted'>{nutrient.label}</Text>
                    <Text>{nutrient.display}</Text>
                  </Flex>
                ))}
              </HStack>
            )}
          </Stack>
        ) : nutrients.length > 0 ? (
          <HStack gap={6} mt={{ base: 4, lg: 0 }} mb={4}>
            {nutrients.map((nutrient, index) => (
              <Flex key={index} fontSize='sm' flexDir='column' gap={0}>
                <Text color='text.muted'>{nutrient.label}</Text>
                <Text>{nutrient.display}</Text>
              </Flex>
            ))}
          </HStack>
        ) : null}

        {data?.rate ? (
          <HStack pt={{ base: 0, lg: 1 }} gap={1}>
            <HStack gap={1} alignItems='center'>
              <Rating
                readOnly
                style={{ maxWidth: 90 }}
                value={data.rate || 0}
                itemStyles={{
                  itemShapes: RoundedStar,
                  activeFillColor: 'var(--rate-color)',
                  inactiveFillColor: 'var(--gray-color-400)',
                }}
              />
            </HStack>
            <Comments reviews={reviews?.reviews} count={reviews?.count} />
          </HStack>
        ) : (
          data?.id && (
            <HStack pt={{ base: 0, lg: 1 }} gap={1}>
              <CRating recipeId={data.id} />
            </HStack>
          )
        )}

        {data?.cook_time > 0 && (
          <HStack mt={5} flexWrap='wrap'>
            <HStack
              gap={2}
              alignItems='center'
              borderRadius='lg'
              bg='white'
              w='fit-content'
              fontSize='xs'
              px={2}
              py={1}
            >
              <ClockIcon size={16} color='var(--primary-default)' />
              <Text fontWeight={500}>
                {t('cooking_time', { minute: data.cook_time })}
              </Text>
            </HStack>
          </HStack>
        )}

        {data?.author && (
          <Flex mt={3} fontSize='sm' flexDir='column' gap={0}>
            <Text color='text.muted'>{t('author')}</Text>
            <Text fontWeight={500}>{data.author}</Text>
          </Flex>
        )}

        {descriptionText && (
          <>
            <Box
              mb={2}
              mt={4}
              fontSize='sm'
              lineHeight='18px'
              position='relative'
            >
              {showMore || !isLongDescription ? (
                <Box
                  color='gray.600'
                  className='recipe-description'
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : (
                <Text color='gray.600' whiteSpace='pre-wrap'>
                  {descriptionText.substring(0, 140)}
                </Text>
              )}
            </Box>
            {isLongDescription && (
              <Text
                w='fit-content'
                color='primary'
                fontSize='xs'
                as={Link}
                fontWeight={400}
                transform='translateY(-5px)'
                onClick={() => setShowMore((prev) => !prev)}
              >
                {showMore ? t('show_less') : t('show_more')}
              </Text>
            )}
          </>
        )}
      </Stack>

      {data?.ingredients?.length > 0 && (
        <Box mt={{ base: 6, lg: 4 }}>
          <Text fontSize='lg' fontWeight={600} mb={3}>
            {t('required_ingredients')}
          </Text>
          <VStack align='stretch' gap={2}>
            {data.ingredients.map((item, index) =>
              item?.[currentLocale] ? (
                <HStack key={index} align='flex-start' gap={3}>
                  <Box
                    mt='7px'
                    w='6px'
                    h='6px'
                    rounded='full'
                    bg='primary'
                    flexShrink={0}
                  />
                  <Text fontSize='sm' lineHeight='20px'>
                    {item[currentLocale]}
                  </Text>
                </HStack>
              ) : null,
            )}
          </VStack>
        </Box>
      )}
    </Stack>
  );
}
